/**
 * Leaderboard window (plan 23 row 40, lane BM) — registry id `leaderboard`,
 * opened with the `leaderboard` binding (O) or the quick menu. Reads the two
 * public, server-cached endpoints; the server refreshes the top list once a
 * minute, so the window simply fetches on open, on tab change and on ↻, and
 * again on your own level-up. WindowHost draws it in the Tome frame (Tome UI
 * Round 5, E4), whose panel carries the zone's --tome-* tokens.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../../../../../config';
import {
  LEADERBOARD_ONLINE_PATH,
  LEADERBOARD_TOP_PATH,
  type LeaderboardOnlineResponse,
  type LeaderboardTopResponse,
} from '../../../../../shared/social/socialWire';
import { EventBus, EVENTS } from '../../../../../shared/EventBus';
import { useOptionalGame } from '../../../contexts/GameContext';
import { LeaderboardView, type LeaderboardTab } from './LeaderboardView';
import '../SocialTome.css';
import './LeaderboardTome.css';

async function fetchJson<T>(url: string, signal: AbortSignal, bypassCache = false): Promise<T> {
  const finalUrl = bypassCache ? `${url}${url.includes('?') ? '&' : '?'}refresh=1&t=${Date.now()}` : url;
  const response = await fetch(finalUrl, {
    signal,
    cache: bypassCache ? 'no-cache' : 'default',
  });
  if (!response.ok) throw new Error(`Request failed (${response.status})`);
  return response.json() as Promise<T>;
}

export const LeaderboardContent: React.FC = () => {
  const selfName = useOptionalGame()?.player?.name ?? null;
  const [tab, setTab] = useState<LeaderboardTab>('top');
  const [top, setTop] = useState<LeaderboardTopResponse | null>(null);
  const [online, setOnline] = useState<LeaderboardOnlineResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    const bus = EventBus.getInstance();
    const unsub = bus.on(EVENTS.PLAYER_LEVEL_UP, () => {
      refresh();
    });
    return unsub;
  }, [refresh]);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    const path = tab === 'top' ? LEADERBOARD_TOP_PATH : LEADERBOARD_ONLINE_PATH;
    fetchJson<LeaderboardTopResponse | LeaderboardOnlineResponse>(
      `${API_URL}${path}`,
      controller.signal,
      refreshKey > 0,
    )
      .then(body => {
        if (tab === 'top') setTop(body as LeaderboardTopResponse);
        else setOnline(body as LeaderboardOnlineResponse);
      })
      .catch(err => {
        if ((err as Error)?.name !== 'AbortError') setError('The leaderboard could not be read. Try ↻ in a moment.');
      });
    return () => controller.abort();
  }, [tab, refreshKey]);

  return (
    <div className="leaderboard-ledger" data-testid="leaderboard-content">
      <LeaderboardView tab={tab} onTabChange={setTab} top={top?.entries ?? null} selfName={selfName}
        online={online ? { total: online.total, players: online.players } : null} error={error} onRefresh={refresh} />
    </div>
  );
};
